import React, { useState } from 'react';

import { DateOwnerTag } from './Tags';
import Dropdown from 'react-bootstrap/Dropdown';
import TextAreaInputContainer from './field_inputs/text/TextAreaInputContainer';
import DisplayAreaText from './field_inputs/text/DisplayAreaText';
import { getIcon } from './field_inputs/shared/FormatHelpers';
import { IsHtml } from './Functions';
import DOMPurify from 'dompurify';
import { ITEM_ACTIVITIES, ITEM_ALL, ITEM_CHANGELOG, ITEM_FILES, ITEM_NOTES } from '../../../i18n/keyConstants';
import { useTranslation } from 'react-i18next';


export const HistoryPanel = ({
    historyCollection,
    activeItem,
    activeHistoryContent,
    setActiveHistoryContent,
    removeNote,
    updateNote,
    removeFile,
    removeActivity,
    downloadFile,
}) => {
    return (
        <>
            <div className='d-flex flex-column position-relative'>
                <div>
                    <HistoryOptions
                        historyCollection={historyCollection}
                        activeHistoryContent={activeHistoryContent}
                        setActiveHistoryContent={setActiveHistoryContent}
                    />
                </div>
            </div>
            <div className='d-flex flex-column position-relative timeline-solid'>
                <HistoryObjects
                    activeHistoryContent={activeHistoryContent}
                    historyCollection={historyCollection}
                    activeItem={activeItem}
                    removeNote={removeNote}
                    updateNote={updateNote}
                    removeFile={removeFile}
                    removeActivity={removeActivity}
                    downloadFile={downloadFile}
                />
            </div>
        </>
    );
};

const HistoryOptions = ({ historyCollection, activeHistoryContent, setActiveHistoryContent }) => {
    const { t } = useTranslation('historyItems');
    return (
        <div id='history-choices' className='d-flex flex-row my-3 mx-auto justify-content-center' style={{ width: '80%' }}>
            <HistoryOption
                content={t(ITEM_ALL)}
                index={0}
                activeHistoryContent={activeHistoryContent}
                setActiveHistoryContent={setActiveHistoryContent}
            />
            <HistoryOption
                content={t(ITEM_ACTIVITIES)}
                index={1}
                disabled={historyCollection.activities.length === 0}
                activeHistoryContent={activeHistoryContent}
                setActiveHistoryContent={setActiveHistoryContent}
            />
            <HistoryOption
                content={t(ITEM_NOTES)}
                index={2}
                disabled={historyCollection.notes.length === 0}
                activeHistoryContent={activeHistoryContent}
                setActiveHistoryContent={setActiveHistoryContent}
            />
            <HistoryOption
                content={t(ITEM_FILES)}
                index={5}
                disabled={historyCollection.files.length === 0}
                activeHistoryContent={activeHistoryContent}
                setActiveHistoryContent={setActiveHistoryContent}
            />
            <HistoryOption
                content={t(ITEM_CHANGELOG)}
                index={6}
                disabled={historyCollection.changelogs.length === 0}
                activeHistoryContent={activeHistoryContent}
                setActiveHistoryContent={setActiveHistoryContent}
            />
        </div>
    );
};

const HistoryOption = ({ content, activeHistoryContent, disabled = false, index, setActiveHistoryContent }) => {
    return (
        <label
            type='button'
            className={`btn btn-light d-flex justify-content-center align-items-center mb-0 ${
                activeHistoryContent === index ? 'active-history-choice' : ''
                } ${disabled ? 'disabled' : ''}`}
            onClick={() => {
                if (!disabled) {
                    setActiveHistoryContent(index);
                }
            }}>
            <p className='mr-1 mb-0'>{content}</p>
        </label>
    );
};

const HistoryObjects = ({
    activeHistoryContent,
    historyCollection,
    activeItem,
    removeNote,
    updateNote,
    removeActivity,
    removeFile,
    downloadFile,
}) => {
    return (
        <>
            {OrderHistoryObjects(activeHistoryContent, historyCollection).map((object, index) => {
                return (
                    <HistoryObject
                        key={'history-object-' + index}
                        object={object}
                        index={index}
                        activeItem={activeItem}
                        removeNote={removeNote}
                        updateNote={updateNote}
                        removeActivity={removeActivity}
                        removeFile={removeFile}
                        downloadFile={downloadFile}
                    />
                );
            })}
        </>
    );
};

const HistoryObject = ({ object, index, activeItem, removeNote, updateNote, removeActivity, removeFile, downloadFile }) => {
    switch (object.generalType) {
        case 'activity':
            return <ActivityHistoryItem item={object} index={index} activeItem={activeItem} removeActivity={removeActivity} />;
        case 'note':
            return <NoteHistoryItem item={object} index={index} activeItem={activeItem} removeNote={removeNote} updateNote={updateNote} />;
        case 'file':
            return <FileHistoryItem item={object} index={index} removeFile={removeFile} downloadFile={downloadFile}></FileHistoryItem>;
        default:
            return <ChangelogHistoryItem item={object} index={index} activeItem={activeItem} />;
    }
};

const ActivityHistoryItem = ({ item, index, activeItem, removeActivity }) => {
    const activity = item;
    return (
        <div className='position-relative w-100' id={`activity-${item.id}`}>
            <div className='d-flex position-absolute justify-content-center align-items-center' style={{ top: '10px', width: '10%' }}>
                <div
                    className='d-flex justify-content-center align-items-center border bg-white'
                    style={{ borderRadius: '50%', width: 30, height: 30 }}>
                    <span
                        className='d-flex justify-content-center align-items-center oi oi-calendar m-auto'
                        style={{ width: 20, height: 20 }}
                    />
                </div>
            </div>
            <div key={'activity-history-' + index} className='d-flex flex-column card ml-auto p-2 mb-2' style={{ width: '90%' }}>
                <div className='d-flex flex-row'>
                    <h5 className='mb-0'>
                        {activity.activityType && activity.activityType.name ? activity.activityType.name : 'Okänd Aktivitet'}
                    </h5>
                    {activity.name && activity.name.length && <h5 className='mb-0'>&nbsp;-&nbsp;"{activity.name}"</h5>}
                    <Dropdown className='ml-auto mr-1'>
                        <Dropdown.Toggle as={CustomOptionsDropdownToggle}>
                            <button
                                className='btn btn-light ml-auto my-auto mr-1 d-flex justify-content-center align-items-center border-0 bg-white'
                                style={{ width: 25, height: 25, borderRadius: '25%' }}>
                                <span className='oi oi-ellipses'></span>
                            </button>
                        </Dropdown.Toggle>
                        <Dropdown.Menu>
                            <Dropdown.Item onClick={() => removeActivity(activity)}>Ta Bort</Dropdown.Item>
                        </Dropdown.Menu>
                    </Dropdown>
                </div>
                <div className='d-flex flex-row text-center align-items-center'>
                    <DateOwnerTag isColorActive={false} date={activity['date']} user={activity.owner} itemType='activity' />
                    {activity.activityPersons.length > 0 ? (
                        <>
                            <p className='d-flex justify-content-center align-items-center my-auto ml-2'>{getIcon('person')}</p>
                            {activity.activityPersons.map((value, index) => {
                                return (
                                    <React.Fragment key={index}>
                                        <div className='mr-1' />
                                        {value.person.link && <a href={value.person.link}>{value.person?.name ?? ''}</a>}
                                        {!value.person.link && <p>{value.person?.name ?? ''}</p>}
                                        {index < activity.activityPersons.length - 1 ? ',' : ''}
                                    </React.Fragment>
                                );
                            })}
                        </>
                    ) : null}
                    {activity.deal && activity.deal?.link && (
                        <p className='d-flex justify-content-center align-items-center my-auto ml-2'>
                            {' '}
                            {getIcon('deal')}{' '}
                            <a className='ml-1' href={activity.deal.link}>
                                {activity.deal.name ?? 'Deal'}
                            </a>
                        </p>
                    )}
                </div>
                <div className='mt-1'>
                    <ActivityTextInput {...{ notes: activity.notes }} />
                </div>
            </div>
        </div>
    );
};

const ActivityTextInput = ({ notes }) => {
    if (IsHtml(notes)) {
        return <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(notes) }}></div>;
    }
    return <DisplayAreaText {...{ value: notes }} />;
};

const CustomOptionsDropdownToggle = React.forwardRef(({ children, onClick }, ref) => (
    <div
        ref={ref}
        onClick={(e) => {
            e.preventDefault();
            onClick(e);
        }}>
        {children}
    </div>
));

const NoteHistoryItem = ({ item, index, activeItem, removeNote, updateNote }) => {
    const [isEditing, setIsEditingNote] = useState(false);
    const updateNoteLocal = (newValue, note) => {
        item.text = newValue;
        return updateNote(newValue, note);
    };
    return (
        <div className='position-relative w-100'>
            <div className='d-flex position-absolute justify-content-center align-items-center' style={{ top: '10px', width: '10%' }}>
                <div
                    className='d-flex justify-content-center align-items-center border bg-white'
                    style={{ borderRadius: '50%', width: 30, height: 30 }}>
                    <span
                        className='d-flex justify-content-center align-items-center oi oi-comment-square m-auto'
                        style={{ width: 20, height: 20 }}
                    />
                </div>
            </div>
            <div key={'note-history-' + index} className='d-flex flex-column card ml-auto p-2 mb-2 bg-yellow' style={{ width: '90%' }}>
                <div className='d-flex flex-row'>
                    <DateOwnerTag isColorActive={false} date={item.createdDate} user={item.createdUser} itemType='note' />
                    <Dropdown className='ml-auto mr-1'>
                        <Dropdown.Toggle as={CustomOptionsDropdownToggle}>
                            <button
                                className='btn btn-light ml-auto my-auto mr-1 d-flex justify-content-center align-items-center border-0 bg-yellow'
                                style={{ width: 25, height: 25, borderRadius: '25%' }}>
                                <span className='oi oi-ellipses'></span>
                            </button>
                        </Dropdown.Toggle>
                        <Dropdown.Menu>
                            <Dropdown.Item onClick={() => setIsEditingNote(true)}>Redigera</Dropdown.Item>
                            <Dropdown.Item onClick={() => removeNote(item)}>Ta Bort</Dropdown.Item>
                        </Dropdown.Menu>
                    </Dropdown>
                </div>
                <TextInput
                    {...{
                        initialItem: item,
                        initialValue: item.text,
                        isEditing: isEditing,
                        setIsEditing: setIsEditingNote,
                        syncChanges: updateNoteLocal,
                        cancelMessage: true,
                    }}
                />
            </div>
        </div>
    );
};

const TextInput = ({ initialItem, initialValue, isEditing, setIsEditing, syncChanges, cancelMessage }) => {
    if (IsHtml(initialValue)) {
        if (isEditing) {
            return (
                <TextAreaInputContainer
                    className='mb-0'
                    {...{
                        initialItem: initialItem,
                        initialValue: initialValue,
                        isEditing: isEditing,
                        setIsEditing: setIsEditing,
                        syncChanges: syncChanges,
                        cancelMessage: cancelMessage,
                    }}
                />
            );
        } else {
            return <div className='mb-0' dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(initialValue) }}></div>;
        }
    }
    return (
        <TextAreaInputContainer
            className='mb-0'
            {...{
                initialItem: initialItem,
                initialValue: initialValue,
                isEditing: isEditing,
                setIsEditing: setIsEditing,
                syncChanges: syncChanges,
                cancelMessage: cancelMessage,
            }}
        />
    );
};

const FileHistoryItem = ({ item, index, removeFile, downloadFile }) => {
    return (
        <div className='position-relative w-100' id={`file-${item.id}`}>
            <div className='d-flex position-absolute justify-content-center align-items-center' style={{ top: '10px', width: '10%' }}>
                <div
                    className='d-flex justify-content-center align-items-center border bg-white'
                    style={{ borderRadius: '50%', width: 30, height: 30 }}>
                    <span
                        className='d-flex justify-content-center align-items-center oi oi-file m-auto'
                        style={{ width: 20, height: 20 }}
                    />
                </div>
            </div>
            <div key={'file-history-' + index} className='d-flex flex-column card ml-auto p-2 mb-2' style={{ width: '90%' }}>
                <div className='d-flex flex-row'>
                    <DateOwnerTag isColorActive={false} date={item.createdDate} user={item.owner} itemType='file' />
                    <Dropdown className='ml-auto mr-1'>
                        <Dropdown.Toggle as={CustomOptionsDropdownToggle}>
                            <button
                                className='btn btn-light ml-auto my-auto mr-1 d-flex justify-content-center align-items-center border-0'
                                style={{ width: 25, height: 25, borderRadius: '25%' }}>
                                <span className='oi oi-ellipses'></span>
                            </button>
                        </Dropdown.Toggle>
                        <Dropdown.Menu>
                            <Dropdown.Item onClick={() => downloadFile(item)}>Ladda ner</Dropdown.Item>
                            <Dropdown.Item onClick={() => removeFile(item)}>Ta Bort</Dropdown.Item>
                        </Dropdown.Menu>
                    </Dropdown>
                </div>
                <div>
                    <a className='file-download-link' target='_blank' href={`api/crmFileFront/${item.id}/download`}>
                        <b>
                            <i className='oi oi-data-transfer-download' />
                            {item.name}
                        </b>
                    </a>
                </div>
            </div>
        </div>
    );
};

const ChangelogHistoryItem = ({ item, index, activeItem }) => {
    return (
        <div className='position-relative w-100'>
            <div className='d-flex position-absolute justify-content-center align-items-center' style={{ top: '10px', width: '10%' }}>
                <div
                    className='d-flex justify-content-center align-items-center border bg-white'
                    style={{ borderRadius: '50%', width: 20, height: 20 }}>
                    <span className='position-relative m-auto' style={{ left: 2, width: 15, height: 15 }} />
                </div>
            </div>
            <div key={'changelog-history-' + index} className='d-flex flex-column ml-auto p-2 mb-2' style={{ width: '90%' }}>
                {activeItem.id != item.organizationId ? (
                    <h6 className='mb-0 text-muted'>Tillhör organisation {item.organization?.name}</h6>
                ) : (
                    <></>
                )}
                <h5 className='mb-0'>
                    {`${item.title}: ${item.oldText} `}
                    {getIcon('arrow-right')}
                    {` ${item.text}`}
                </h5>
                <DateOwnerTag isColorActive={false} date={item['date']} user={item.user} itemType='changeLog' />
            </div>
        </div>
    );
};

const OrderHistoryObjects = (activeHistoryContent, historyObjects) => {
    let orderedObjects = [];

    for (const [key, value] of Object.entries(historyObjects)) {
        switch (activeHistoryContent) {
            case 0:
                value.map((elem) => {
                    if (key === 'changelogs') {
                        elem = {
                            ...elem,
                            createdDate: elem.date,
                        };
                    }
                    if (key === 'activities') elem.createdDate = elem.date ? elem.date : elem.createdDate;

                    if (elem.path !== 'LostReason') {
                        orderedObjects.push(elem);
                    }
                });
                break;
            case 1:
                if (key === 'activities') {
                    value.map((elem) => {
                        elem.createdDate = elem.date ? elem.date : elem.createdDate;
                        orderedObjects.push(elem);
                    });
                }
                break;
            case 2:
                if (key === 'notes') {
                    value.map((elem) => orderedObjects.push(elem));
                }
                break;
            case 3:
                if (key === 'emails') {
                    value.map((elem) => orderedObjects.push(elem));
                }
                break;
            case 4:
                if (key === 'invoices') {
                    value.map((elem) => orderedObjects.push(elem));
                }
                break;
            case 5:
                if (key === 'files') {
                    value.map((elem) => orderedObjects.push(elem));
                }
                break;
            default:
                if (key === 'changelogs') {
                    value.map((elem) => {
                        if (key === 'changelogs') {
                            elem = {
                                ...elem,
                                createdDate: elem.date,
                            };
                        }
                        if (elem.path !== 'LostReason') {
                            orderedObjects.push(elem);
                        }
                    });
                }
                break;
        }
    }

    orderedObjects.sort((elem1, elem2) => new Date(elem2.createdDate) - new Date(elem1.createdDate));

    return orderedObjects;
};
